/**
 * Browser watch over the relay (GET /v1/users/{user_id}/browser/watch): a read-only live view of the tabs the
 * browser fetch opens on the box (never a person's window, never the agent's own tab), for a screen the caller shows.
 * Server ↔ runner only, never forwarded to a browser.
 *
 * The server holds one watch per user while anyone watches, as a lease: BROWSER_WATCH_START names the watch and its
 * rules and is sent again every BROWSER_WATCH_RENEW_MS, so a runner that restarted picks it up within that time; a
 * runner that hears nothing for leaseMs stops on its own. BROWSER_WATCH_STOP ends it at once. Watching never wakes the
 * box and never holds it awake: the server sends a start only to a runner already connected.
 *
 * The runner answers with BROWSER_WATCH_EVENT frames: a tab opened, a picture of it, a tab hidden (it reached a
 * blocked path or left the allowed sites: it sends nothing more until it closes), a tab closed.
 */
import { z } from 'zod';

/** How often the server renews a watch, and how long a runner keeps one it has heard nothing more of. */
export const BROWSER_WATCH_RENEW_MS = 20_000;
export const BROWSER_WATCH_LEASE_MS = 60_000;
/** Pictures a second across every watched tab of a box, and the most a viewer may ask for. */
export const BROWSER_WATCH_DEFAULT_FPS = 6;
export const BROWSER_WATCH_MAX_FPS = 10;
/** A picture's width in pixels when one tab is watched; with more, each is half that. */
export const BROWSER_WATCH_DEFAULT_WIDTH = 960;
export const BROWSER_WATCH_MAX_WIDTH = 1280;

const WatchId = z.string().min(1).max(80);
const TabId = z.string().min(1).max(80);

/** Server → runner (BROWSER_WATCH_START): apps/server platform/compute/browser-watch.ts, to a connected runner only. */
export const BrowserWatchStartPayloadSchema = z.object({
  watchId: WatchId,
  fps: z.number().int().min(1).max(BROWSER_WATCH_MAX_FPS),
  width: z.number().int().min(320).max(BROWSER_WATCH_MAX_WIDTH),
  /** The deployment's browser.allowed_domains: a picture of any other site is never sent. */
  allowedDomains: z.array(z.string()).max(200),
  /** The deployment's browser.blocked_paths: a tab that reaches one sends nothing more (browserPathBlocked). */
  blockedPaths: z.array(z.string()).max(200),
  leaseMs: z.number().int().min(5_000).max(300_000),
});
export type BrowserWatchStartPayload = z.infer<typeof BrowserWatchStartPayloadSchema>;

/** Server → runner (BROWSER_WATCH_STOP): nobody watches any more. */
export const BrowserWatchStopPayloadSchema = z.object({ watchId: WatchId });
export type BrowserWatchStopPayload = z.infer<typeof BrowserWatchStopPayloadSchema>;

/** The tag of the fetch that has the tab now (the caller's label, on its browser fetch), when it sent one. */
const Tag = z.string().max(64).optional();

/** Runner → server (BROWSER_WATCH_EVENT): apps/sandbox browser/watch.ts. */
export const BrowserWatchEventPayloadSchema = z.discriminatedUnion('kind', [
  z.object({ watchId: WatchId, kind: z.literal('tab_open'), tabId: TabId, tag: Tag }),
  z.object({
    watchId: WatchId,
    kind: z.literal('frame'),
    tabId: TabId,
    tag: Tag,
    /** The page the picture shows, on an allowed site and on no blocked path. */
    url: z.string(),
    /** Epoch ms when the runner sent it. */
    at: z.number().int(),
    width: z.number().int().nonnegative(),
    height: z.number().int().nonnegative(),
    jpegBase64: z.string().max(2_000_000),
  }),
  z.object({ watchId: WatchId, kind: z.literal('hidden'), tabId: TabId, tag: Tag, reason: z.enum(['blocked_path', 'off_site']) }),
  z.object({ watchId: WatchId, kind: z.literal('tab_close'), tabId: TabId, tag: Tag }),
]);
export type BrowserWatchEventPayload = z.infer<typeof BrowserWatchEventPayloadSchema>;
